import { useState } from "react";
import {
  IIdentityBlock,
  IManualIdentityPreview,
} from "@/@types/divergence";
import {
  useLazyPreviewManualIdentityQuery,
  usePreviewManualIdentityQuery,
} from "@/services/divergence";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";

import { IDENTITY_REPLACEMENT, identityBlockOf } from "../utils/manual-identity";
import { DECISION_LABELS, MANUAL_IDENTITY_LABELS } from "../utils/messages";

interface ManualIdentityFallbackState {
  block: IIdentityBlock | null;
  preview: IManualIdentityPreview | null;
  resolved: IManualIdentityPreview | null;
  resolvedError: string | null;
  searchError: string | null;
  isSearching: boolean;
  isDescribing: boolean;
  candidatesFailed: boolean;
  offerFor: (error: unknown) => boolean;
  search: (cpf: string) => Promise<void>;
  chooseCandidate: (operatorCode: string) => Promise<void>;
  startReplacement: () => void;
  reset: () => void;
}

export function useManualIdentityFallback(
  divergenceId: number,
  options: { enabled?: boolean } = {},
): ManualIdentityFallbackState {
  const [manualBlock, setManualBlock] = useState<IIdentityBlock | null>(null);
  const [preview, setPreview] = useState<IManualIdentityPreview | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [candidatesFailed, setCandidatesFailed] = useState<boolean>(false);

  const {
    data: resolved,
    error: describeError,
    isFetching: isDescribing,
  } = usePreviewManualIdentityQuery(
    { divergence_id: divergenceId },
    { skip: !options.enabled },
  );

  const [runPreview, { isFetching }] = useLazyPreviewManualIdentityQuery();

  const offered: IIdentityBlock | null = identityBlockOf(describeError);

  const block: IIdentityBlock | null =
    manualBlock ?? (offered?.manual_identity_allowed ? offered : null);

  const resolvedError: string | null =
    describeError && !block
      ? extractRtkErrorMessage(describeError, DECISION_LABELS.ERROR)
      : null;

  const offerFor = (error: unknown): boolean => {
    const candidate: IIdentityBlock | null = identityBlockOf(error);

    if (!candidate?.manual_identity_allowed) return false;

    setManualBlock(candidate);
    setPreview(null);
    setSearchError(null);
    setCandidatesFailed(false);

    return true;
  };

  const lookup = async (
    query: { cpf?: string; operatorCode?: string },
    onFailure?: () => void,
  ): Promise<void> => {
    setSearchError(null);

    try {
      const found: IManualIdentityPreview = await runPreview({
        divergence_id: divergenceId,
        ...(query.cpf ? { cpf: query.cpf } : {}),
        ...(query.operatorCode ? { operator_code: query.operatorCode } : {}),
      }).unwrap();

      setPreview(found);
    } catch (error) {
      setPreview(null);
      setSearchError(extractRtkErrorMessage(error, DECISION_LABELS.ERROR));
      onFailure?.();
    }
  };

  const search = async (cpf: string): Promise<void> => {
    await lookup({ cpf });
  };

  const chooseCandidate = async (operatorCode: string): Promise<void> => {
    await lookup({ operatorCode }, (): void => setCandidatesFailed(true));
  };

  const startReplacement = (): void => {
    setManualBlock({
      identity_failure: IDENTITY_REPLACEMENT,
      manual_identity_allowed: true,
      message: MANUAL_IDENTITY_LABELS.REPLACEMENT_INTRO,
      candidates: [],
      knownOperator: null,
    });
    setPreview(null);
    setSearchError(null);
    setCandidatesFailed(false);
  };

  const reset = (): void => {
    setManualBlock(null);
    setPreview(null);
    setSearchError(null);
    setCandidatesFailed(false);
  };

  return {
    block,
    preview,
    resolved: resolved ?? null,
    resolvedError,
    searchError,
    isSearching: isFetching,
    isDescribing,
    candidatesFailed,
    offerFor,
    search,
    chooseCandidate,
    startReplacement,
    reset,
  };
}
